import React, { useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Typography,
} from '@mui/material'
import {
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { alpha } from '@mui/material/styles'
import { useQuery } from '@tanstack/react-query'
import { ref, getDownloadURL } from 'firebase/storage'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import type { DamageReport, DamageReportItem } from '@otw/models'
import type { PhotoTaskTemplate } from '@otw/models/photos/photoTaskTemplate'
import { storage } from '@/config/firebase'
import { useAdminTransporter } from '@/hooks/admin/useAdminTransporter'

const SLOT_HEIGHT = 200

interface LegMediaItem {
  id: string
  angle: string
  url?: string
  storagePath?: string
  mediaType?: 'photo' | 'video'
  isReplaced?: boolean
  replacedAt?: unknown
  originalUploadedAt?: unknown
}

const formatMediaTimestamp = (value: unknown): string | null => {
  if (!value) return null
  if (value instanceof Date) {
    const ms = value.getTime()
    return Number.isNaN(ms) ? null : value.toLocaleString()
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toLocaleString()
  }
  if (typeof value === 'object') {
    const maybeTs = value as {
      toDate?: () => Date
      seconds?: number
      _seconds?: number
    }
    if (typeof maybeTs.toDate === 'function') {
      const date = maybeTs.toDate()
      return Number.isNaN(date.getTime()) ? null : date.toLocaleString()
    }
    const seconds = maybeTs._seconds ?? maybeTs.seconds
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000).toLocaleString()
    }
  }
  return null
}

const ReplacedMediaBanner: React.FC<{ item: LegMediaItem }> = ({ item }) => {
  const original = formatMediaTimestamp(item.originalUploadedAt)
  const replaced = formatMediaTimestamp(item.replacedAt)

  return (
    <Box
      sx={{
        mt: 0.75,
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: 'warning.50',
        border: '1px solid',
        borderColor: 'warning.200',
      }}
    >
      <Typography variant="caption" fontWeight={600} color="warning" display="block">
        Replaced after original upload
      </Typography>
      {original && (
        <Typography variant="caption" color="text.secondary" display="block">
          Original: {original}
        </Typography>
      )}
      {replaced && (
        <Typography variant="caption" color="text.secondary" display="block">
          Replaced: {replaced}
        </Typography>
      )}
    </Box>
  )
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value)

const severityColor = (severity: DamageReportItem['severity']) =>
  severity === 'minor' ? 'success' : severity === 'moderate' ? 'warning' : 'error'

const PhotoDamageReport: React.FC<{ report: DamageReport | null | undefined; hasPhoto: boolean }> = ({
  report,
  hasPhoto,
}) => {
  const [expanded, setExpanded] = useState(false)

  if (!hasPhoto) return null
  if (report == null) {
    return <Chip size="small" label="Not inspected" variant="outlined" sx={{ mt: 0.75 }} />
  }

  const items = report.damage_report ?? []
  const noDamage = report.no_damage_detected
  const hasDetails = !!report.overall_explanation || items.length > 0

  return (
    <Box
      sx={{
        mt: 0.75,
        borderRadius: 1,
        border: '1px solid',
        borderColor: (theme) =>
          alpha(noDamage ? theme.palette.success.main : theme.palette.warning.main, 0.4),
        bgcolor: (theme) =>
          alpha(noDamage ? theme.palette.success.main : theme.palette.warning.main, 0.08),
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={hasDetails ? () => setExpanded((prev) => !prev) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: 0.75,
          cursor: hasDetails ? 'pointer' : 'default',
          '&:hover': hasDetails ? { bgcolor: 'action.hover' } : undefined,
        }}
      >
        <Chip
          size="small"
          label={noDamage ? 'No damage' : items.length === 1 ? '1 issue' : `${items.length} issues`}
          color={noDamage ? 'success' : 'error'}
          variant="outlined"
          sx={{ bgcolor: 'background.paper' }}
        />
        {!noDamage && (
          <Typography variant="caption" fontWeight={600} color="warning.dark" noWrap>
            {formatCurrency(report.total_cost_low)} – {formatCurrency(report.total_cost_high)}
          </Typography>
        )}
        {hasDetails && (
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              ml: 'auto',
              color: 'text.secondary',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {items.map((item: DamageReportItem, index: number) => (
            <Box
              key={`${item.part}-${index}`}
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>
                  {item.part}
                </Typography>
                <Chip
                  size="small"
                  label={capitalize(item.severity)}
                  color={severityColor(item.severity)}
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                />
              </Box>
              {item.explanation && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {item.explanation}
                </Typography>
              )}
              <Typography variant="caption" color="primary.main" fontWeight={600} display="block">
                {formatCurrency(item.cost_low)} – {formatCurrency(item.cost_high)}
              </Typography>
            </Box>
          ))}

          {report.overall_explanation && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontStyle: 'italic' }}
            >
              {report.overall_explanation}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

interface LegPhotoAdminUploadGridProps {
  planId: string
  legIndex: number
  flow: 'preDelivery' | 'postDelivery'
  template: PhotoTaskTemplate | null
  photos: LegMediaItem[]
  videos: LegMediaItem[]
  selectedPhotos?: Record<string, boolean>
  onPhotoSelect?: (id: string) => void
  onPhotoClick?: (photo: LegMediaItem) => void
  onVideoClick?: (video: LegMediaItem) => void
  /** AI damage reports keyed by photo id */
  damageReportsByPhotoId?: Record<string, DamageReport | null>
}

export const LegPhotoAdminUploadGrid: React.FC<LegPhotoAdminUploadGridProps> = ({
  planId,
  legIndex,
  flow,
  template,
  photos,
  videos,
  selectedPhotos = {},
  onPhotoSelect,
  onPhotoClick,
  onVideoClick,
  damageReportsByPhotoId,
}) => {
  const { uploadAdminLegPhoto, uploadAdminLegVideo } = useAdminTransporter()
  const [uploadingPhoto, setUploadingPhoto] = useState<string | null>(null)
  const [uploadingVideo, setUploadingVideo] = useState<string | null>(null)
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const activeTasks = useMemo(
    () =>
      (template?.tasks ?? [])
        .filter((t) => t.active)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [template]
  )

  const photoPositions = useMemo(
    () => activeTasks.filter((t) => t.mediaType !== 'video'),
    [activeTasks]
  )
  const videoPositions = useMemo(
    () => activeTasks.filter((t) => t.mediaType === 'video'),
    [activeTasks]
  )

  const { data: exampleUrlByTaskId = {} } = useQuery({
    queryKey: ['leg_photo_task_examples', template?.id, flow, photoPositions.map((p) => p.id), videoPositions.map((v) => v.id)],
    queryFn: async () => {
      const urls: Record<string, string> = {}
      for (const task of activeTasks) {
        const path = task.mediaType === 'video' ? task.videoStoragePath : task.storagePath
        if (!path) continue
        try {
          urls[task.id] = await getDownloadURL(ref(storage, path))
        } catch {
          // ignore missing example assets
        }
      }
      return urls
    },
    enabled: activeTasks.length > 0,
    staleTime: 1000 * 60 * 60,
  })

  const triggerFileInput = (angle: string, mediaType: 'photo' | 'video') => {
    const key = `${mediaType}:${angle}`
    fileInputRefs.current[key]?.click()
  }

  const handlePhotoUpload = async (angle: string, file: File) => {
    setUploadingPhoto(angle)
    try {
      await uploadAdminLegPhoto(planId, legIndex, angle, file, flow)
    } finally {
      setUploadingPhoto(null)
    }
  }

  const handleVideoUpload = async (angle: string, file: File) => {
    setUploadingVideo(angle)
    try {
      await uploadAdminLegVideo(planId, legIndex, angle, file, flow)
    } finally {
      setUploadingVideo(null)
    }
  }

  const handleFileInputChange = (
    angle: string,
    mediaType: 'photo' | 'video',
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files
    if (!files?.length) return
    if (mediaType === 'photo') {
      void handlePhotoUpload(angle, files[0])
    } else {
      void handleVideoUpload(angle, files[0])
    }
    e.target.value = ''
  }

  if (activeTasks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No photo template tasks configured for this transport company.
      </Typography>
    )
  }

  const stopCardClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const renderPhotoSlot = (position: (typeof photoPositions)[number]) => {
    const photo = photos.find((p) => p.angle === position.displayName)
    const exampleUrl = exampleUrlByTaskId[position.id]
    const isUploading = uploadingPhoto === position.displayName

    return (
      <Grid item xs={12} sm={6} md={4} key={position.id}>
        <Box>
          <Box
            sx={{
              height: SLOT_HEIGHT,
              border: '2px solid',
              borderColor: photo ? 'success.main' : 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease',
              '&:hover': photo
                ? {
                    borderColor: 'primary.main',
                    transform: 'scale(1.02)',
                    boxShadow: 2,
                  }
                : undefined,
            }}
          >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bgcolor: photo ? 'success.main' : 'grey.500',
              color: 'white',
              px: 1,
              py: 0.5,
              zIndex: 2,
            }}
          >
            <Typography variant="caption" fontWeight={600} noWrap>
              {position.displayName}
            </Typography>
          </Box>

          {photo?.url || photo?.storagePath ? (
            <>
              <Box
                sx={{ flex: 1, pt: 3, position: 'relative', cursor: 'pointer' }}
                onClick={() => onPhotoClick?.(photo)}
              >
                <LazyLoadImage
                  src={photo.url || photo.storagePath || ''}
                  alt={position.displayName}
                  effect="blur"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              {onPhotoSelect && (
                <Box
                  sx={{ position: 'absolute', top: 28, right: 4, zIndex: 3 }}
                  onClick={stopCardClick}
                  onMouseDown={stopCardClick}
                  onPointerDown={stopCardClick}
                >
                  <Checkbox
                    size="small"
                    checked={!!selectedPhotos[photo.id]}
                    onChange={() => onPhotoSelect(photo.id)}
                    sx={{
                      color: 'white',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      borderRadius: '4px',
                      '&.Mui-checked': {
                        color: 'primary.main',
                        bgcolor: 'rgba(255,255,255,0.9)',
                      },
                    }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  right: 8,
                  zIndex: 3,
                  display: 'flex',
                  justifyContent: 'center',
                }}
                onClick={stopCardClick}
                onMouseDown={stopCardClick}
                onPointerDown={stopCardClick}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  startIcon={
                    isUploading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={(e) => {
                    stopCardClick(e)
                    triggerFileInput(position.displayName, 'photo')
                  }}
                  onMouseDown={stopCardClick}
                  onPointerDown={stopCardClick}
                  disabled={isUploading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    bgcolor: 'rgba(0,0,0,0.65)',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  {isUploading ? 'Replacing...' : 'Replace'}
                </Button>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pt: 3,
                px: 1,
                gap: 1,
              }}
            >
              {exampleUrl ? (
                <Box
                  component="img"
                  src={exampleUrl}
                  alt={`Example: ${position.displayName}`}
                  sx={{
                    width: '100%',
                    maxHeight: 90,
                    objectFit: 'contain',
                    borderRadius: 0.5,
                    opacity: 0.85,
                  }}
                />
              ) : (
                <CancelIcon sx={{ fontSize: 36, opacity: 0.3 }} />
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                onClick={(e) => {
                  stopCardClick(e)
                  triggerFileInput(position.displayName, 'photo')
                }}
                disabled={isUploading}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          )}

          <input
            ref={(el) => {
              fileInputRefs.current[`photo:${position.displayName}`] = el
            }}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleFileInputChange(position.displayName, 'photo', e)}
          />
          </Box>
          {photo && damageReportsByPhotoId && (
            <PhotoDamageReport
              hasPhoto
              report={damageReportsByPhotoId[photo.id]}
            />
          )}
          {photo?.isReplaced && <ReplacedMediaBanner item={photo} />}
        </Box>
      </Grid>
    )
  }

  const renderVideoSlot = (position: (typeof videoPositions)[number]) => {
    const video = videos.find((v) => v.angle === position.displayName)
    const isUploading = uploadingVideo === position.displayName

    return (
      <Grid item xs={12} sm={6} md={4} key={position.id}>
        <Box>
          <Box
            sx={{
              height: SLOT_HEIGHT,
              border: '2px solid',
              borderColor: video ? 'success.main' : 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bgcolor: video ? 'success.main' : 'grey.500',
              color: 'white',
              px: 1,
              py: 0.5,
              zIndex: 2,
            }}
          >
            <Typography variant="caption" fontWeight={600} noWrap>
              {position.displayName}
            </Typography>
          </Box>

          {video?.url || video?.storagePath ? (
            <Box sx={{ flex: 1, pt: 3, position: 'relative' }}>
              <Box
                component="video"
                src={video.url || video.storagePath}
                controls
                playsInline
                onClick={() => onVideoClick?.(video)}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  right: 8,
                  zIndex: 3,
                  display: 'flex',
                  justifyContent: 'center',
                }}
                onClick={stopCardClick}
                onMouseDown={stopCardClick}
                onPointerDown={stopCardClick}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  startIcon={
                    isUploading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={(e) => {
                    stopCardClick(e)
                    triggerFileInput(position.displayName, 'video')
                  }}
                  onMouseDown={stopCardClick}
                  onPointerDown={stopCardClick}
                  disabled={isUploading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    bgcolor: 'rgba(0,0,0,0.65)',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  {isUploading ? 'Replacing...' : 'Replace'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pt: 3,
                px: 1,
                gap: 1,
              }}
            >
              {position.placeholderText && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textAlign: 'center', px: 1 }}
                >
                  {position.placeholderText}
                </Typography>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                onClick={(e) => {
                  stopCardClick(e)
                  triggerFileInput(position.displayName, 'video')
                }}
                disabled={isUploading}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          )}

          <input
            ref={(el) => {
              fileInputRefs.current[`video:${position.displayName}`] = el
            }}
            type="file"
            accept="video/*"
            style={{ display: 'none' }}
            onChange={(e) => handleFileInputChange(position.displayName, 'video', e)}
          />
          </Box>
          {video?.isReplaced && <ReplacedMediaBanner item={video} />}
        </Box>
      </Grid>
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload or replace on behalf of the transport company when they cannot complete photos
        themselves.
      </Typography>

      {photoPositions.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Photos
          </Typography>
          <Grid container spacing={2} sx={{ mb: videoPositions.length > 0 ? 3 : 0 }}>
            {photoPositions.map(renderPhotoSlot)}
          </Grid>
        </>
      )}

      {videoPositions.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Videos
          </Typography>
          <Grid container spacing={2}>
            {videoPositions.map(renderVideoSlot)}
          </Grid>
        </>
      )}
    </Box>
  )
}

export default LegPhotoAdminUploadGrid
